import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, CheckCircle, ArrowRight, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import DensityDetectionPanel from '@/components/issues/DensityDetectionPanel';

export default function Phase3Density({ issues, officers, onProceed, onIssueClassified }) {
  const [expandedIssue, setExpandedIssue] = useState(null);

  const pendingDensity = issues.filter((i) =>
  i.classification_gate1 === 'signal' && (!i.classification_gate2 || i.classification_gate2 === 'pending')
  );
  const classifiedDensity = issues.filter((i) =>
  i.classification_gate1 === 'signal' && i.classification_gate2 && i.classification_gate2 !== 'pending'
  );

  const balloonCount = classifiedDensity.filter((i) => i.classification_gate2 === 'balloon').length;
  const rockCount = classifiedDensity.filter((i) => i.classification_gate2 === 'rock').length;
  const allDone = pendingDensity.length === 0;

  const handleClassified = (issueId) => {
    setExpandedIssue(null);
    onIssueClassified?.(issueId);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white">Phase 3: Density Assessment</h2>
        <p className="text-sm text-slate-300">15-25 minutes — Classify signals as Balloon or Rock</p>
      </div>

      {/* Running Tally */}
      <div className="flex items-center justify-center gap-6 p-3 rounded-lg bg-slate-800/60 border border-slate-700">
        <span className="text-sm text-slate-300">
          Assessed: <span className="font-bold text-white">{classifiedDensity.length}/{classifiedDensity.length + pendingDensity.length}</span>
        </span>
        <span className="text-sm text-slate-400">|</span>
        <span className="text-sm text-slate-200">Balloons: <span className="font-bold text-sky-300">🎈 {balloonCount}</span></span>
        <span className="text-sm text-slate-200">Rocks: <span className="font-bold text-white">🪨 {rockCount}</span></span>
      </div>

      {/* Pending Density Issues */}
      <div className="space-y-2">
        {pendingDensity.map((issue) => {
          const isExpanded = expandedIssue === issue.id;
          const reporter = officers.find((o) => o.user_email === issue.reported_by);

          return (
            <Card key={issue.id} className="glass-panel border-slate-800">
              <Collapsible open={isExpanded} onOpenChange={() => setExpandedIssue(isExpanded ? null : issue.id)}>
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-amber-400" /> : <ChevronRight className="w-4 h-4 text-amber-400" />}
                      <div className="flex-1 text-left">
                        <p className="text-[10px] text-slate-500 font-mono select-text cursor-text" onClick={(e) => e.stopPropagation()}>{issue.id}</p>
                        <p className="text-slate-700 text-sm font-medium">{issue.title}</p>
                        <p className="text-orange-600 mt-0.5 text-xs">Signal Score: {issue.gate1_score}/6</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') : issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : '-'}
                        </p>
                      </div>
                      <Badge className="bg-amber-500/20 text-amber-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">Signal</Badge>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    {issue.description && <p className="text-slate-700 text-sm">{issue.description}</p>}
                    <div className="border-t border-slate-700 pt-4">
                      <DensityDetectionPanel issue={issue} onComplete={() => handleClassified(issue.id)} />
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>);

        })}
      </div>

      {/* Classified */}
      {classifiedDensity.length > 0 &&
      <div className="space-y-1">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">Assessed</p>
          {classifiedDensity.map((issue) =>
        <div key={issue.id} className="flex items-center gap-2 p-2 rounded bg-slate-800/30">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <div className="flex-1">
                <p className="text-[10px] text-slate-500 font-mono select-text cursor-text">{issue.id}</p>
                <span className="text-sm text-slate-300">{issue.title}</span>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') : issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : ''}
                </p>
              </div>
              <Badge className={issue.classification_gate2 === 'balloon' ? 'bg-sky-500/20 text-sky-300' : 'bg-stone-600/20 text-white'}>
                {issue.classification_gate2 === 'balloon' ? '🎈 Balloon' : '🪨 Rock'}
              </Badge>
            </div>
        )}
        </div>
      }

      <Button onClick={onProceed} disabled={!allDone} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-base disabled:opacity-50">
        {allDone ? 'Proceed to Resolution Routing' : `${pendingDensity.length} signals remaining`}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>);

}